import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
} from '@mui/material';
import {
  Edit,
  Add,
  VisibilityOff,
  RestoreFromTrash,
  Save,
  Close,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { Product, ProductFormData, ProductImportResult } from '../../types';
import { useAuth } from '../../context/AuthContext';
import { CatalogListToolbar } from '../common/CatalogListToolbar';
import {
  SortableFilterableHeader,
  FreeSoloAutocompleteField,
  getDistinctValues,
} from '../common/SortableFilterableHeader';

// `unidad` dropped from sort (worker brief "catalog-list-filter-polish",
// 2026-09-06) - it keeps its column filter below, just not sortable.
type SortableColumn = 'codigo' | 'grupo' | 'categoria' | 'descripcion' | 'color' | 'proveedor';

// Columnas con filtro por valores distintos (worker brief
// "catalog-list-ui-pattern", 2026-09-05) - exactamente las pedidas: grupo,
// categoria, color, proveedor, unidad, y "estado" (derivado de
// is_active/is_outlet, ver getEstadoLabel - el Product model real no tiene
// un campo de texto "estado" propio, ver CLAUDE.md §7/backend
// product_service.py).
type FilterableColumn = 'grupo' | 'categoria' | 'color' | 'proveedor' | 'unidad' | 'estado';

const ESTADO_ACTIVO = 'Activo';
const ESTADO_OUTLET = 'Outlet';
const ESTADO_INACTIVO = 'Inactivo';

// "Estado" del producto tal como realmente vive en el modelo hoy: dos
// booleanos (is_active/is_outlet), no una columna de texto - ver
// backend/src/models/__init__.py y product_service.py. Solo existen 3
// estados distinguibles en el schema actual (ACTIVO y A PEDIDO colapsan al
// mismo is_active=true/is_outlet=false - CLAUDE.md §7), así que el filtro de
// "Estado" ofrece exactamente esos 3, no 4.
const getEstadoLabel = (product: Product): string => {
  if (!product.is_active) return ESTADO_INACTIVO;
  if (product.is_outlet) return ESTADO_OUTLET;
  return ESTADO_ACTIVO;
};

export const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth(); // Obtener usuario actual
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false); // Separate loading for search
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Ordenamiento y filtro por columna (worker brief
  // "catalog-list-ui-pattern") - reemplaza el bloque de "Ordenar por" +
  // botones Outlet/Solo Inactivos que vivía arriba de la tabla; ahora vive
  // en cada encabezado de columna vía SortableFilterableHeader.
  const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  // Multi-select, OR-combined per column (worker brief
  // "catalog-list-filter-polish", 2026-09-06) - empty array = unfiltered.
  const [columnFilters, setColumnFilters] = useState<Record<FilterableColumn, string[]>>({
    grupo: [],
    categoria: [],
    color: [],
    proveedor: [],
    unidad: [],
    estado: [],
  });

  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50); // 50 productos por página por defecto

  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; product: Product | null; permanent: boolean }>({
    open: false,
    product: null,
    permanent: false
  });

  // Edición inline (pedido directo del usuario, ver CLAUDE.md worker brief
  // "admin-inline-edit"): lápiz junto al código -> la fila se vuelve
  // editable con inputs -> Guardar/Cancelar. Solo una fila editable a la
  // vez (elección simple, documentada en el reporte de esta rama) - abrir
  // otra fila cancela silenciosamente la anterior sin persistir cambios a
  // medio hacer. SKU se retiró del set editable junto con la columna
  // (worker brief "catalog-list-ui-pattern") - ver HANDOFF.md.
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Record<string, string>>({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Importar catálogo (CSV/XLS) - reemplaza el sync en vivo con Google
  // Sheets retirado para Materiales (CLAUDE.md §7).
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ProductImportResult | null>(null);

  // Helper function to format dimensions (all in meters)
  const formatDimensions = (product: Product) => {
    const dimensions = [];
    if (product.largo) dimensions.push(`L:${product.largo}m`);
    if (product.ancho) dimensions.push(`A:${product.ancho}m`);
    if (product.espesor) dimensions.push(`E:${product.espesor}m`);

    if (dimensions.length === 0) return '-';

    // Si hay más de una dimensión, mostrarlas en líneas separadas
    if (dimensions.length > 1) {
      return (
        <Box component="span" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
          {dimensions.map((dim, index) => (
            <Typography key={index} variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1.2, fontFamily: 'monospace' }}>
              {dim}
            </Typography>
          ))}
        </Box>
      );
    }

    // Si solo hay una dimensión, mostrarla directamente
    return (
      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}>
        {dimensions[0]}
      </Typography>
    );
  };

  // Distintos valores por columna (worker brief "catalog-list-ui-pattern"):
  // computados client-side sobre el dataset ya cargado (`products`, que
  // ahora siempre trae todos los estados - ver loadProducts) - alimenta
  // tanto el dropdown de filtro del encabezado como el Autocomplete
  // freeSolo de edición inline de la misma columna.
  const grupoOptions = getDistinctValues(products, 'grupo');
  const categoriaOptions = getDistinctValues(products, 'categoria');
  const colorOptions = getDistinctValues(products, 'color');
  const proveedorOptions = getDistinctValues(products, 'proveedor');
  const unidadOptions = getDistinctValues(products, 'unidad');
  const estadoOptions = Array.from(new Set(products.map(getEstadoLabel))).sort();

  const handleColumnFilterChange = (column: FilterableColumn, values: string[]) => {
    setColumnFilters(prev => ({ ...prev, [column]: values }));
    setCurrentPage(1);
  };

  const handleSort = (column: SortableColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  // Filtrado por columna, seguido de ordenamiento por columna (o el orden
  // original de carga si no hay ninguna columna de orden activa).
  const filteredProducts = products.filter((product) => {
    if (columnFilters.grupo.length > 0 && !columnFilters.grupo.includes(product.grupo || '')) return false;
    if (columnFilters.categoria.length > 0 && !columnFilters.categoria.includes(product.categoria || '')) return false;
    if (columnFilters.color.length > 0 && !columnFilters.color.includes(product.color || '')) return false;
    if (columnFilters.proveedor.length > 0 && !columnFilters.proveedor.includes(product.proveedor || '')) return false;
    if (columnFilters.unidad.length > 0 && !columnFilters.unidad.includes(product.unidad || '')) return false;
    if (columnFilters.estado.length > 0 && !columnFilters.estado.includes(getEstadoLabel(product))) return false;
    return true;
  });

  const sortedProducts = React.useMemo(() => {
    if (!sortColumn) return filteredProducts;
    const withCollator = [...filteredProducts].sort((a, b) => {
      const aValue = (a[sortColumn] as string) || '';
      const bValue = (b[sortColumn] as string) || '';
      return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' });
    });
    return sortDirection === 'asc' ? withCollator : withCollator.reverse();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredProducts, sortColumn, sortDirection]);

  // Cálculos de paginación
  const totalProducts = sortedProducts.length;
  const totalPages = Math.ceil(totalProducts / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedProducts = sortedProducts.slice(startIndex, endIndex);

  // Reset página cuando cambia la búsqueda o filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, itemsPerPage]);

  useEffect(() => {
    loadProducts(true); // Initial load
  }, []);

  useEffect(() => {
    if (debouncedSearchTerm !== undefined) {
      loadProducts(false); // Search load
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadProducts = async (isInitialLoad = false) => {
    try {
      // Only show main loading spinner on initial load
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setSearchLoading(true);
      }
      setError(null);

      const filters = {
        ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
        // Worker brief "catalog-list-ui-pattern": el filtro de Estado ahora
        // vive en el encabezado de columna y filtra client-side, así que
        // necesitamos los 3 estados (activo/outlet/inactivo) presentes de
        // entrada - `all_statuses` es la única forma de pedir eso en una
        // sola llamada (ver product_service.py's list_products).
        all_statuses: true,
        limit: 1000, // Solicitar hasta 1000 productos
      };
      const response = await apiService.products.getAll(filters);

      // Verificar estructura de respuesta
      if (response && Array.isArray(response.items)) {
        setProducts(response.items);
      } else if (Array.isArray(response)) {
        // Si la respuesta es directamente un array
        setProducts(response);
      } else {
        console.warn('Unexpected response structure:', response);
        setProducts([]);
      }
    } catch (err: any) {
      console.error('Error loading products:', err);
      setError('Error al cargar los productos. Verifique que el backend esté funcionando.');
      setProducts([]); // Asegurar que products no sea undefined
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset página al buscar
  };

  // Campos editables inline (coinciden con las columnas visibles de la
  // tabla, salvo STOCK -- vive en su propia tabla/endpoint,
  // PUT /products/{id}/stock, por eso queda fuera de este pase -- y Estado,
  // que ya tiene su propio botón dedicado de activar/desactivar). SKU se
  // retiró de este set junto con la columna (worker brief
  // "catalog-list-ui-pattern") - sigue siendo editable desde ProductForm.tsx,
  // usado por los formularios de presupuesto, así que no se tocó el tipo
  // Product/ProductFormData, solo este formulario inline.
  const handleStartEdit = (product: Product) => {
    setEditingProductId(product.id);
    setEditError(null);
    setEditForm({
      codigo: product.codigo ?? '',
      grupo: product.grupo ?? '',
      categoria: product.categoria ?? '',
      descripcion: product.descripcion ?? '',
      color: product.color ?? '',
      proveedor: product.proveedor ?? '',
      unidad: product.unidad ?? '',
      largo: product.largo != null ? String(product.largo) : '',
      ancho: product.ancho != null ? String(product.ancho) : '',
      espesor: product.espesor != null ? String(product.espesor) : '',
      estructura_m2: product.estructura_m2 ?? '',
    });
  };

  const handleCancelEdit = () => {
    setEditingProductId(null);
    setEditForm({});
    setEditError(null);
  };

  const handleEditFieldChange = (field: string, value: string) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveEdit = async (product: Product) => {
    if (!editForm.codigo.trim()) {
      setEditError('El código no puede estar vacío');
      return;
    }
    try {
      setSavingEdit(true);
      setEditError(null);
      const payload: Partial<ProductFormData> = {
        codigo: editForm.codigo.trim(),
        grupo: editForm.grupo.trim(),
        categoria: editForm.categoria.trim(),
        descripcion: editForm.descripcion.trim(),
        color: editForm.color.trim(),
        proveedor: editForm.proveedor.trim(),
        unidad: editForm.unidad.trim(),
        largo: editForm.largo.trim() !== '' ? Number(editForm.largo) : undefined,
        ancho: editForm.ancho.trim() !== '' ? Number(editForm.ancho) : undefined,
        espesor: editForm.espesor.trim() !== '' ? Number(editForm.espesor) : undefined,
        estructura_m2: editForm.estructura_m2,
      };
      const updated = await apiService.products.update(product.id, payload);
      setProducts(prev => prev.map(p => (p.id === product.id ? updated : p)));
      setEditingProductId(null);
      setEditForm({});
    } catch (err: any) {
      console.error('Error saving inline product edit:', err);
      setEditError(err.response?.data?.detail || 'Error al guardar los cambios');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeletePermanently = (product: Product) => {
    setDeleteDialog({ open: true, product, permanent: true });
  };

  const confirmDelete = async () => {
    if (!deleteDialog.product) return;

    try {
      await apiService.products.deletePermanently(deleteDialog.product.id);
      // Eliminación permanente siempre quita el producto de la lista
      setProducts(products.filter(p => p.id !== deleteDialog.product!.id));
      setDeleteDialog({ open: false, product: null, permanent: false });
    } catch (err: any) {
      console.error('Error deleting product:', err);
      setError('Error al eliminar el producto');
    }
  };

  const handleToggleActive = async (product: Product) => {
    try {
      // Partial update - ProductUpdate solo aplica los campos presentes.
      await apiService.products.update(product.id, { is_active: !product.is_active });

      // Actualizar el producto en la lista local
      setProducts(products.map(p =>
        p.id === product.id ? { ...p, is_active: !p.is_active } : p
      ));
    } catch (err: any) {
      console.error('Error updating product:', err);
      setError('Error al actualizar el producto');
    }
  };

  const handleReactivate = async (product: Product) => {
    try {
      await apiService.products.update(product.id, { is_active: true });

      // Actualizar el producto en la lista local
      setProducts(products.map(p =>
        p.id === product.id ? { ...p, is_active: true } : p
      ));
    } catch (err: any) {
      console.error('Error reactivating product:', err);
      setError('Error al reactivar el producto');
    }
  };

  const handleCreateProduct = () => {
    navigate('/products/new');
  };

  const handleOpenImportDialog = () => {
    setImportFile(null);
    setImportResult(null);
    setImportDialog(true);
  };

  const confirmImport = async () => {
    if (!importFile) return;
    try {
      setImporting(true);
      const result = await apiService.products.importCatalog(importFile);
      setImportResult(result);
      setImportDialog(false);
      await loadProducts();
    } catch (err: any) {
      console.error('Error importing catalog:', err);
      setImportResult({
        created: 0,
        updated: 0,
        errors: [err.response?.data?.detail || 'Error al importar el catálogo'],
      });
      setImportDialog(false);
    } finally {
      setImporting(false);
    }
  };

  // Worker brief "catalog-list-ui-pattern": plantilla CSV de arranque para
  // el importador de arriba. Se descarga vía Blob (no un <a href> directo)
  // porque la autenticación acá es un header Bearer sobre la instancia de
  // axios, no una cookie - un link navegado directo por el browser no lo
  // llevaría.
  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.products.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'plantilla_productos.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error downloading template:', err);
      setError('Error al descargar la plantilla');
    }
  };

  const columnCount = user?.role === 'admin' ? 12 : 11;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (

    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <CatalogListToolbar
        title="Productos"
        itemNoun="Productos"
        isAdmin={user?.role === 'admin'}
        onImportClick={handleOpenImportDialog}
        onDownloadTemplate={handleDownloadTemplate}
        searchValue={searchTerm}
        onSearchChange={handleSearchChange}
        searchLoading={searchLoading}
        searchPlaceholder="Buscar productos por código, descripción..."
        subtitle={
          <>
            {totalProducts > 0 ? (
              <>
                Mostrando {startIndex + 1}-{Math.min(endIndex, totalProducts)} de {totalProducts} producto{totalProducts !== 1 ? 's' : ''}
                {totalPages > 1 && ` (Página ${currentPage} de ${totalPages})`}
              </>
            ) : (
              'No hay productos para mostrar'
            )}
            {searchTerm && ` (filtrado por: "${searchTerm}")`}
          </>
        }
        extraControls={
          <FormControl sx={{ minWidth: '120px' }} size="small">
            <InputLabel>Por página</InputLabel>
            <Select
              value={itemsPerPage}
              label="Por página"
              onChange={(e) => {
                setItemsPerPage(e.target.value as number);
                setCurrentPage(1);
              }}
            >
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
              <MenuItem value={200}>200</MenuItem>
              <MenuItem value={500}>Todos</MenuItem>
            </Select>
          </FormControl>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {editError && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setEditError(null)}>
          {editError}
        </Alert>
      )}

      <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 24px 0 rgba(60,72,100,0.10)', overflowX: 'auto' }}>
        <Table>
          <TableHead sx={{ backgroundColor: 'rgb(66,154,45)' }}>
            <TableRow>
              <SortableFilterableHeader
                label="CODIGO"
                align="left"
                onSort={() => handleSort('codigo')}
                sortActive={sortColumn === 'codigo'}
                sortDirection={sortDirection}
              />
              <SortableFilterableHeader
                label="GRUPO"
                onSort={() => handleSort('grupo')}
                sortActive={sortColumn === 'grupo'}
                sortDirection={sortDirection}
                filterOptions={grupoOptions}
                filterValue={columnFilters.grupo}
                onFilterChange={(v) => handleColumnFilterChange('grupo', v)}
              />
              <SortableFilterableHeader
                label="CAT."
                filterLabel="Categoría"
                onSort={() => handleSort('categoria')}
                sortActive={sortColumn === 'categoria'}
                sortDirection={sortDirection}
                filterOptions={categoriaOptions}
                filterValue={columnFilters.categoria}
                onFilterChange={(v) => handleColumnFilterChange('categoria', v)}
              />
              <SortableFilterableHeader
                label="DESCRIPCIÓN"
                onSort={() => handleSort('descripcion')}
                sortActive={sortColumn === 'descripcion'}
                sortDirection={sortDirection}
              />
              <SortableFilterableHeader
                label="COLOR"
                onSort={() => handleSort('color')}
                sortActive={sortColumn === 'color'}
                sortDirection={sortDirection}
                filterOptions={colorOptions}
                filterValue={columnFilters.color}
                onFilterChange={(v) => handleColumnFilterChange('color', v)}
              />
              <SortableFilterableHeader
                label="PROVEEDOR"
                onSort={() => handleSort('proveedor')}
                sortActive={sortColumn === 'proveedor'}
                sortDirection={sortDirection}
                filterOptions={proveedorOptions}
                filterValue={columnFilters.proveedor}
                onFilterChange={(v) => handleColumnFilterChange('proveedor', v)}
              />
              <SortableFilterableHeader
                label="UNIDAD"
                filterOptions={unidadOptions}
                filterValue={columnFilters.unidad}
                onFilterChange={(v) => handleColumnFilterChange('unidad', v)}
              />
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>STOCK</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>DIMENSIONES (m)</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>ESTRUCTURA POR M2</TableCell>
              <SortableFilterableHeader
                label="Estado"
                filterOptions={estadoOptions}
                filterValue={columnFilters.estado}
                onFilterChange={(v) => handleColumnFilterChange('estado', v)}
              />
              {user?.role === 'admin' && (
                <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Acciones</TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedProducts && paginatedProducts.length > 0 ? paginatedProducts.map((product, index) => (
              <TableRow
                key={product.id}
                sx={{
                  backgroundColor: index % 2 === 0 ? 'white' : '#f5f5f5',
                  transition: 'background 0.2s',
                  '&:hover': {
                    backgroundColor: '#e0f2fe' // celeste muy claro
                  }
                }}
              >
                {editingProductId === product.id ? (
                  <>
                    <TableCell>
                      <TextField size="small" variant="standard" value={editForm.codigo} required
                        error={!editForm.codigo.trim()}
                        onChange={(e) => handleEditFieldChange('codigo', e.target.value)} />
                    </TableCell>
                    <TableCell align="center">
                      <FreeSoloAutocompleteField
                        value={editForm.grupo}
                        options={grupoOptions}
                        onChange={(v) => handleEditFieldChange('grupo', v)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <FreeSoloAutocompleteField
                        value={editForm.categoria}
                        options={categoriaOptions}
                        onChange={(v) => handleEditFieldChange('categoria', v)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField size="small" variant="standard" value={editForm.descripcion}
                        onChange={(e) => handleEditFieldChange('descripcion', e.target.value)} sx={{ minWidth: 160 }} />
                    </TableCell>
                    <TableCell align="center">
                      <FreeSoloAutocompleteField
                        value={editForm.color}
                        options={colorOptions}
                        onChange={(v) => handleEditFieldChange('color', v)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <FreeSoloAutocompleteField
                        value={editForm.proveedor}
                        options={proveedorOptions}
                        onChange={(v) => handleEditFieldChange('proveedor', v)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <FreeSoloAutocompleteField
                        value={editForm.unidad}
                        options={unidadOptions}
                        onChange={(v) => handleEditFieldChange('unidad', v)}
                      />
                    </TableCell>
                    <TableCell align="center">{product.stock ?? '-'}</TableCell>
                    <TableCell align="center">
                      <Box display="flex" flexDirection="column" gap={0.5}>
                        <TextField size="small" variant="standard" type="number" label="Largo"
                          value={editForm.largo} inputProps={{ step: 0.001, min: 0 }}
                          onChange={(e) => handleEditFieldChange('largo', e.target.value)} />
                        <TextField size="small" variant="standard" type="number" label="Ancho"
                          value={editForm.ancho} inputProps={{ step: 0.001, min: 0 }}
                          onChange={(e) => handleEditFieldChange('ancho', e.target.value)} />
                        <TextField size="small" variant="standard" type="number" label="Espesor"
                          value={editForm.espesor} inputProps={{ step: 0.001, min: 0 }}
                          onChange={(e) => handleEditFieldChange('espesor', e.target.value)} />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <TextField size="small" variant="standard" multiline minRows={2} value={editForm.estructura_m2}
                        placeholder="codigo,cantidad"
                        onChange={(e) => handleEditFieldChange('estructura_m2', e.target.value)} sx={{ minWidth: 160 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center" flexWrap="wrap">
                        <Chip
                          label={product.is_active ? 'Activo' : 'Inactivo'}
                          color={product.is_active ? 'success' : 'default'}
                          size="small"
                        />
                        {product.is_outlet && <Chip label="Outlet" color="warning" size="small" />}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <IconButton
                          size="small"
                          onClick={() => handleSaveEdit(product)}
                          color="success"
                          disabled={savingEdit || !editForm.codigo.trim()}
                          title="Guardar cambios"
                        >
                          <Save />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={handleCancelEdit}
                          disabled={savingEdit}
                          title="Cancelar edición"
                        >
                          <Close />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{product.codigo}</TableCell>
                    <TableCell align="center">{product.grupo || '-'}</TableCell>
                    <TableCell align="center">{product.categoria || '-'}</TableCell>
                    <TableCell align="center">{product.descripcion || '-'}</TableCell>
                    <TableCell align="center">{product.color || '-'}</TableCell>
                    <TableCell align="center">{product.proveedor || '-'}</TableCell>
                    <TableCell align="center">{product.unidad || '-'}</TableCell>
                    <TableCell align="center">{product.stock ?? '-'}</TableCell>
                    <TableCell align="center">
                      {formatDimensions(product)}
                    </TableCell>
                    <TableCell align="center">
                      {product.estructura_m2 && product.estructura_m2.trim() ? (
                        <Box sx={{ maxWidth: 200, overflow: 'auto', textAlign: 'left', mx: 'auto' }}>
                          {product.estructura_m2.split('\n').map((line, idx) => {
                            const [codigo, cantidad] = line.split(',');
                            return (
                              <Typography key={idx} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                                {codigo ? codigo.trim() : ''}{cantidad ? ` × ${cantidad.trim()}` : ''}
                              </Typography>
                            );
                          })}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center" flexWrap="wrap">
                        <Chip
                          label={product.is_active ? 'Activo' : 'Inactivo'}
                          color={product.is_active ? 'success' : 'default'}
                          size="small"
                        />
                        {product.is_outlet && <Chip label="Outlet" color="warning" size="small" />}
                      </Box>
                    </TableCell>
                    {user?.role === 'admin' && (
                      <TableCell align="center">
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <IconButton
                            size="small"
                            onClick={() => handleStartEdit(product)}
                            color="primary"
                            title="Editar producto"
                          >
                            <Edit />
                          </IconButton>
                          {product.is_active ? (
                            <IconButton
                              size="small"
                              onClick={() => handleToggleActive(product)}
                              color="warning"
                              title="Desactivar producto"
                            >
                              <VisibilityOff />
                            </IconButton>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => handleReactivate(product)}
                              color="success"
                              title="Reactivar producto"
                            >
                              <RestoreFromTrash />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </>
                )}
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={columnCount} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No hay productos para mostrar
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Controles de paginación */}
      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={2}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, page) => setCurrentPage(page)}
            color="primary"
            showFirstButton
            showLastButton
          />
          <Typography variant="body2" color="text.secondary">
            {startIndex}-{endIndex} de {sortedProducts.length} productos (Página {currentPage} de {totalPages})
          </Typography>
        </Box>
      )}

      {(!products || products.length === 0) && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No se encontraron productos
          </Typography>
        </Box>
      )}

      {/* Dialog de confirmación para eliminar */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, product: null, permanent: false })}
      >
        <DialogTitle>Confirmar eliminación permanente</DialogTitle>
        <DialogContent>
          <Typography>
            ¿Estás seguro de que deseas eliminar PERMANENTEMENTE el producto "{deleteDialog.product?.codigo}"?
            Esta acción no se puede deshacer y el producto se eliminará completamente de la base de datos.
            Los presupuestos existentes mantendrán la información del producto.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, product: null, permanent: false })}>
            Cancelar
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Eliminar Permanentemente
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de importación de catálogo (CSV/XLS) - CLAUDE.md §7 */}
      <Dialog
        open={importDialog}
        onClose={() => !importing && setImportDialog(false)}
      >
        <DialogTitle>Importar catálogo (CSV/XLS)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Columnas esperadas (no distingue mayúsculas): CODIGO, DESCRIPCION, PROVEEDOR, GRUPO,
            CATEGORIA, UNIDAD, COLOR, LARGO, ANCHO, ESPESOR, ANCHO UTIL, PESO, CONVERSIONM2, ESTADO,
            ESTRUCTURA POR M2, AH, MOQ M2.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No incluye precios (esos se importan aparte, en Listas de Precios) y no desactiva
            productos ausentes del archivo - desactivar sigue siendo una acción manual.
          </Typography>
          <Button component="label" variant="outlined" fullWidth>
            {importFile ? importFile.name : 'Seleccionar archivo'}
            <input
              type="file"
              hidden
              accept=".csv,.xlsx,.xls"
              onChange={(e) => setImportFile(e.target.files?.[0] || null)}
            />
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialog(false)} disabled={importing}>
            Cancelar
          </Button>
          <Button onClick={confirmImport} color="primary" variant="contained" disabled={importing || !importFile}>
            {importing ? 'Importando...' : 'Importar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de resultado de importación */}
      <Dialog
        open={importResult !== null}
        onClose={() => setImportResult(null)}
      >
        <DialogTitle>Resultado de la importación</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="success.main">
            • Creados: {importResult?.created ?? 0}
          </Typography>
          <Typography variant="body2" color="info.main">
            • Actualizados: {importResult?.updated ?? 0}
          </Typography>
          {importResult && importResult.errors.length > 0 && (
            <>
              <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 'bold' }}>
                • Errores ({importResult.errors.length}):
              </Typography>
              <Box sx={{ maxHeight: 200, overflow: 'auto', mt: 1 }}>
                {importResult.errors.map((err, idx) => (
                  <Typography key={idx} variant="caption" display="block" color="error.main">
                    {err}
                  </Typography>
                ))}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResult(null)} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* FAB para crear producto en móvil */}
      {user?.role === 'admin' && (
        <Fab
          color="success"
          aria-label="add"
          onClick={handleCreateProduct}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', sm: 'none' }
          }}
        >
          <Add />
        </Fab>
      )}
    </Box>
  );
};
